import { useState, useEffect } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts'
import api from '../../core/services/api'
import { AlertCircle } from 'lucide-react'

interface CommessaMargine {
  commessa_id: string
  numero_commessa: string
  titolo: string
  cliente_nome: string
  stato: string
  valore_offerta: number
  costi_stimati: number
  margine_percentuale: number
  ore_gantt: number
  ore_preventivate: number
  delta_ore: number
}

const fmt = (n: number) => n >= 1000
  ? `€${(n / 1000).toFixed(0)}k`
  : `€${n.toFixed(0)}`

export default function MarginalitaCommesse() {
  const [data, setData] = useState<CommessaMargine[]>([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'margine' | 'ore'>('margine')

  useEffect(() => {
    api.get('/v1/strategia/marginalita')
      .then((r: { data: CommessaMargine[] }) => setData(r.data))
      .catch(() => setData([]))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-gray-200 border-t-primary-500 rounded-full animate-spin" /></div>
  if (data.length === 0) return <div className="text-center text-gray-400 py-16">Nessun dato disponibile</div>

  const sorted = [...data].sort((a, b) =>
    view === 'margine'
      ? a.margine_percentuale - b.margine_percentuale
      : b.delta_ore - a.delta_ore
  )

  const chartData = sorted.map(c => ({
    nome: c.numero_commessa,
    titolo: c.titolo,
    valore: c.valore_offerta,
    costi: c.costi_stimati,
    margine_pct: c.margine_percentuale,
    delta_ore: c.delta_ore,
    ore_gantt: c.ore_gantt,
    ore_prev: c.ore_preventivate,
  }))

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null
    const d = chartData.find(c => c.nome === label)
    if (!d) return null
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
        <p className="font-bold text-gray-800 mb-1">{d.nome} — {d.titolo}</p>
        {view === 'margine' ? (
          <>
            <p className="text-gray-600">Valore offerta: <strong>{fmt(d.valore)}</strong></p>
            <p className="text-gray-600">Costi stimati: <strong>{fmt(d.costi)}</strong></p>
            <p className={d.margine_pct >= 0 ? 'text-green-600' : 'text-red-600'}>
              Margine: <strong>{d.margine_pct.toFixed(1)}%</strong>
            </p>
          </>
        ) : (
          <>
            <p className="text-gray-600">Ore Gantt: <strong>{d.ore_gantt}g</strong></p>
            <p className="text-gray-600">Ore preventivate: <strong>{d.ore_prev}g</strong></p>
            <p className={d.delta_ore <= 0 ? 'text-green-600' : 'text-red-600'}>
              Delta: <strong>{d.delta_ore > 0 ? '+' : ''}{d.delta_ore}g</strong>
            </p>
          </>
        )}
      </div>
    )
  }

  const totalValore = data.reduce((s, c) => s + c.valore_offerta, 0)
  const avgMargine = data.reduce((s, c) => s + c.margine_percentuale, 0) / data.length
  const atRisk = data.filter(c => c.margine_percentuale < 15).length

  return (
    <div className="space-y-4">
      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Portafoglio totale</p>
          <p className="text-2xl font-bold text-gray-900">{fmt(totalValore)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Margine medio</p>
          <p className={`text-2xl font-bold ${avgMargine >= 20 ? 'text-green-600' : avgMargine >= 10 ? 'text-amber-500' : 'text-red-500'}`}>
            {avgMargine.toFixed(1)}%
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className={`w-8 h-8 ${atRisk > 0 ? 'text-red-400' : 'text-green-400'}`} />
          <div>
            <p className="text-xs text-gray-500">Commesse a rischio</p>
            <p className="text-2xl font-bold text-gray-900">{atRisk}</p>
            <p className="text-xs text-gray-400">margine &lt; 15%</p>
          </div>
        </div>
      </div>

      {/* Chart toggle */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">
          {view === 'margine' ? 'Marginalità per commessa (€)' : 'Delta ore Gantt vs preventivo'}
        </h3>
        <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
          <button onClick={() => setView('margine')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === 'margine' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>
            Margine €
          </button>
          <button onClick={() => setView('ore')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === 'ore' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>
            Delta ore
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <ResponsiveContainer width="100%" height={340}>
          {view === 'margine' ? (
            <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 60, left: 80, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
              <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="nome" tick={{ fontSize: 11 }} width={75} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="costi" stackId="a" name="Costi" fill="#93c5fd" radius={[0, 0, 0, 0]} />
              <Bar dataKey="margine_pct" stackId="b" name="Margine %" fill="#4ade80" radius={[0, 2, 2, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.margine_pct < 0 ? '#f87171' : entry.margine_pct < 15 ? '#fbbf24' : '#4ade80'} />
                ))}
              </Bar>
            </BarChart>
          ) : (
            <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 60, left: 80, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
              <XAxis type="number" tickFormatter={v => `${v}g`} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="nome" tick={{ fontSize: 11 }} width={75} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine x={0} stroke="#6b7280" strokeWidth={1.5} />
              <Bar dataKey="delta_ore" name="Delta ore" radius={[0, 2, 2, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.delta_ore <= 0 ? '#4ade80' : entry.delta_ore <= 10 ? '#fbbf24' : '#f87171'} />
                ))}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-500">Commessa</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Valore</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Costi</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Margine</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Δ Ore</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(c => (
              <tr key={c.commessa_id} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-2">
                  <span className="font-mono font-bold text-gray-500">{c.numero_commessa}</span>
                  <span className="ml-2 text-gray-700">{c.titolo}</span>
                </td>
                <td className="px-4 py-2 text-gray-500">{c.cliente_nome}</td>
                <td className="px-4 py-2 text-right font-medium">{fmt(c.valore_offerta)}</td>
                <td className="px-4 py-2 text-right text-gray-500">{fmt(c.costi_stimati)}</td>
                <td className={`px-4 py-2 text-right font-bold ${c.margine_percentuale < 0 ? 'text-red-500' : c.margine_percentuale < 15 ? 'text-amber-500' : 'text-green-600'}`}>
                  {c.margine_percentuale.toFixed(1)}%
                </td>
                <td className={`px-4 py-2 text-right font-medium ${c.delta_ore > 10 ? 'text-red-500' : c.delta_ore > 0 ? 'text-amber-500' : 'text-green-600'}`}>
                  {c.delta_ore > 0 ? '+' : ''}{c.delta_ore}g
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
